import { ParamMap, Params } from '@angular/router';
import moment from 'moment';


const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const HAS_TZ = /[zZ]$|[+-]\d{2}:\d{2}$|[+-]\d{4}$/;
const ISO_LIKE = /^\d{4}-\d{2}-\d{2}[ T]\d{1,2}:\d{2}/;

function formatMoment(m: moment.Moment): string {
  return m.isValid() ? m.format('DD/MM/YYYY HH:mm:ss') : '-';
}

export function formatCreatedAtForTable(value: Date | string | undefined | null): string {
  if (value == null || value === '') {
    return '-';
  }
  if (typeof value === 'string') {
    const t = value.trim();
    if (DATE_ONLY.test(t)) {
      return formatMoment(moment(t, 'YYYY-MM-DD', true));
    }
    if (ISO_LIKE.test(t) && !HAS_TZ.test(t)) {
      const normalized = t.includes('T') ? t : t.replace(' ', 'T');
      return formatMoment(moment.utc(normalized).local());
    }
  }
  return formatMoment(moment(value));
}


export const TX_LIST_QUERY_KEYS = [
  'page',
  'status',
  'search',
  'transaction_type',
  'trusted',
  'has_open_risk',
  'from_transaction',
  'start_date',
  'end_date',
] as const;

export type TxListQueryKey = (typeof TX_LIST_QUERY_KEYS)[number];

export type TxListQueryShape = Record<TxListQueryKey, string | null>;

export function normalizeTypeFilterFromQuery(raw: unknown, legacyAllToken: string): string {
  if (raw === undefined || raw === null || raw === '') {
    return '';
  }
  const s = String(raw);
  return s === legacyAllToken ? '' : s;
}

function firstQueryValue(v: string | string[] | undefined): string | undefined {
  if (v == null) {
    return undefined;
  }
  return Array.isArray(v) ? v[0] : v;
}

function triStateFromParam(v: string | string[] | undefined): '' | '1' | '0' {
  const x = firstQueryValue(v);
  if (x === '1' || x === '0') {
    return x;
  }
  return '';
}

function parseReturnToTransactionId(fromTx: string | string[] | undefined): string | null {
  const x = firstQueryValue(fromTx);
  if (x == null || x === '') {
    return null;
  }
  const trimmed = x.trim();
  return trimmed ? trimmed : null;
}

export function readListStateFromParams(
  params: Params,
  legacyAllToken: string
): {
  statusFilter: string;
  searchTerm: string;
  typeFilter: string;
  trustedFilter: '' | '1' | '0';
  riskFilter: '' | '1' | '0';
  page: number;
  returnToTransactionId: string | null;
  startDate: string | null;
  endDate: string | null;
} {
  const rawPage = parseInt(String(params['page'] ?? '1'), 10);
  const page = Number.isFinite(rawPage) && rawPage >= 1 ? rawPage : 1;

  const search =
    firstQueryValue(params['search']) ||
    firstQueryValue(params['username']) ||
    '';

  return {
    statusFilter: firstQueryValue(params['status']) || '',
    searchTerm: search,
    typeFilter: normalizeTypeFilterFromQuery(firstQueryValue(params['transaction_type']), legacyAllToken),
    trustedFilter: triStateFromParam(params['trusted']),
    riskFilter: triStateFromParam(params['has_open_risk']),
    page,
    returnToTransactionId: parseReturnToTransactionId(params['from_transaction']),
    startDate: firstQueryValue(params['start_date']) || null,
    endDate: firstQueryValue(params['end_date']) || null,
  };
}

export function snapshotToTxListShape(paramMap: ParamMap): TxListQueryShape {
  const shape = {} as TxListQueryShape;
  for (const key of TX_LIST_QUERY_KEYS) {
    shape[key] = paramMap.get(key);
  }
  if (!shape.page) {
    shape.page = '1';
  }
  return shape;
}

export function shouldSkipTxListNavigation(paramMap: ParamMap, desired: TxListQueryShape): boolean {
  if (paramMap.get('username')) {
    return false;
  }
  return txListQueryShapesEqual(snapshotToTxListShape(paramMap), desired);
}

export function desiredNavigateParamsToShape(
  queryParams: Record<string, string | number | null>
): TxListQueryShape {
  const toStr = (v: string | number | null | undefined): string | null => {
    if (v === null || v === undefined || v === '') {
      return null;
    }
    return String(v);
  };
  return {
    page: toStr(queryParams['page']) ?? '1',
    status: toStr(queryParams['status']),
    search: toStr(queryParams['search']),
    transaction_type: toStr(queryParams['transaction_type']),
    trusted: toStr(queryParams['trusted']),
    has_open_risk: toStr(queryParams['has_open_risk']),
    from_transaction: toStr(queryParams['from_transaction']),
    start_date: toStr(queryParams['start_date']),
    end_date: toStr(queryParams['end_date']),
  };
}

export function txListQueryShapesEqual(a: TxListQueryShape, b: TxListQueryShape): boolean {
  for (const key of TX_LIST_QUERY_KEYS) {
    const va = a[key] ?? null;
    const vb = b[key] ?? null;
    if (va !== vb) {
      return false;
    }
  }
  return true;
}
